import { useContext } from 'react';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';

const useLasertagGame = () => {
  const authCtx = useContext(AuthContext);
  const { isLoading, error, setError, sendRequest } = useHttp();

  const getOperatorContext = async ({ entertainmentId, slot = undefined }) => {
    try {
      const response = await sendRequest({
        url: `/api/v1/lasertag-games/operator/context`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
        params: { entertainmentId, ...(slot !== undefined ? { slot } : {}) },
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const getParticipantsForPreparing = async arg => {
    const slot = typeof arg === 'object' && arg !== null ? arg.slot : arg;
    const entertainmentId =
      typeof arg === 'object' && arg !== null ? arg.entertainmentId : undefined;
    try {
      const params = {
        ...(entertainmentId ? { entertainmentId } : {}),
        ...(slot !== undefined ? { slot } : {}),
      };
      const response = await sendRequest({
        url: `/api/v1/lasertag-games/operator/participants`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
        params,
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const updateSitOutForPreparing = async ({ bookingId, count, entertainmentId } = {}) => {
    try {
      const response = await sendRequest(
        {
          url: `/api/v1/lasertag-games/operator/sitout`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: { bookingId, count, entertainmentId },
        },
        'Fehler beim Speichern der Aussetzer',
        null,
        null,
        null,
        'Gespeichert'
      );
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const updateInformedForPreparing = async ({
    bookingId,
    informed,
    entertainmentId,
  }) => {
    try {
      const response = await sendRequest(
        {
          url: `/api/v1/lasertag-games/operator/informed`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: { bookingId, informed, entertainmentId },
        },
        'Fehler beim Speichern des Informiert-Status',
        null,
        null,
        null,
        'Gespeichert'
      );
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const updateGroupApproval = async ({ approve, bookingIds, entertainmentId } = {}) => {
    try {
      const response = await sendRequest(
        {
          url: `/api/v1/lasertag-games/operator/group-approval`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: { approve, bookingIds, entertainmentId },
        },
        approve ? 'Fehler beim Zulassen der Gruppe' : 'Fehler beim Entfernen der Zulassung',
        null,
        null,
        null,
        approve ? 'Gruppe zugelassen' : 'Zulassung entfernt'
      );
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const confirmPendingCheckin = async arg => {
    const memberId = typeof arg === 'object' && arg !== null ? arg.memberId : arg;
    const entertainmentId =
      typeof arg === 'object' && arg !== null ? arg.entertainmentId : undefined;
    try {
      const response = await sendRequest(
        {
          url: `/api/v1/lasertag-games/operator/confirm-pending`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: { memberId, entertainmentId },
        },
        'Fehler beim Bestätigen des Check-ins',
        null,
        null,
        null,
        'Check-in bestätigt'
      );
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const declinePendingCheckin = async arg => {
    const memberId = typeof arg === 'object' && arg !== null ? arg.memberId : arg;
    const entertainmentId =
      typeof arg === 'object' && arg !== null ? arg.entertainmentId : undefined;
    try {
      const response = await sendRequest(
        {
          url: `/api/v1/lasertag-games/operator/decline-pending`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: { memberId, entertainmentId },
        },
        'Fehler beim Ablehnen des Check-ins',
        null,
        null,
        null,
        'Check-in abgelehnt'
      );
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const getLastAssignmentBySlot = async arg => {
    const slot = typeof arg === 'object' && arg !== null ? arg.slot : arg;
    const entertainmentId =
      typeof arg === 'object' && arg !== null ? arg.entertainmentId : undefined;
    try {
      const response = await sendRequest({
        url: `/api/v1/lasertag-games/operator/last-assignment`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
        params: { slot, entertainmentId },
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const startGame = async ({ gameId, entertainmentId } = {}) => {
    try {
      const body = entertainmentId ? { entertainmentId } : undefined;
      const response = await sendRequest(
        {
          url: `/api/v1/lasertag-games/${gameId}/start`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body,
        },
        'Fehler beim Starten des Spiels',
        null,
        null,
        null,
        'Spiel wurde gestartet'
      );
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const endGame = async ({ gameId, entertainmentId } = {}) => {
    try {
      const body = entertainmentId ? { entertainmentId } : undefined;
      const response = await sendRequest(
        {
          url: `/api/v1/lasertag-games/${gameId}/end`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body,
        },
        'Fehler beim Beenden des Spiels',
        null,
        null,
        null,
        'Spiel wurde beendet'
      );
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const acceptLateCheckin = async arg => {
    const memberId = typeof arg === 'object' && arg !== null ? arg.memberId : arg;
    const entertainmentId =
      typeof arg === 'object' && arg !== null ? arg.entertainmentId : undefined;
    try {
      const response = await sendRequest(
        {
          url: `/api/v1/lasertag-games/operator/late-checkin/accept`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: { memberId, entertainmentId },
        },
        'Fehler beim Hinzufügen des verspäteten Check-ins',
        null,
        null,
        null,
        'Verspäteter Check-in hinzugefügt'
      );
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const updateMember = async ({
    memberId,
    firstName,
    playerName,
    cardStatus,
    cardID,
    appendCardID,
    reprint,
    entertainmentId,
  }) => {
    try {
      const response = await sendRequest(
        {
          url: `/api/v1/members/${memberId}`,
          method: 'PATCH',
          headers: authCtx.getAuthHeaders(),
          body: {
            firstName,
            playerName,
            cardStatus,
            cardID,
            appendCardID,
            reprint,
            ...(entertainmentId ? { entertainmentId } : {}),
          },
        },
        'Fehler beim Aktualisieren der Memberkarte',
        null,
        null,
        null,
        'Memberkarte aktualisiert'
      );
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const getMembersByIds = async ids => {
    try {
      const idList = Array.isArray(ids) ? ids.filter(Boolean) : [];
      if (idList.length === 0) return [];
      const response = await sendRequest({
        url: `/api/v1/members/byIds`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
        params: { ids: idList.join(',') },
      });
      // normalize response
      return response?.data || response || [];
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const updateGroupAssignment = async ({
    bookingId,
    teamNumber,
    teamCount,
    allowDuplicate = false,
    slot = undefined,
    entertainmentId,
  } = {}) => {
    try {
      const response = await sendRequest({
        url: '/api/v1/lasertag-games/assignments',
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body: {
          bookingId,
          teamNumber,
          teamCount,
          allowDuplicate,
          ...(entertainmentId ? { entertainmentId } : {}),
          ...(slot !== undefined ? { slot } : {}),
        },
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const updateSplitQuantities = async ({
    splitQuantities,
    teamCount,
    slot = undefined,
    entertainmentId,
  }) => {
    try {
      const body = {
        splitQuantities,
        teamCount,
        entertainmentId,
        ...(slot !== undefined ? { slot } : {}),
      };
      const response = await sendRequest({
        url: '/api/v1/lasertag-games/assignments/split',
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body,
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const deleteGroupAssignment = async ({
    bookingId,
    teamCount,
    slot = undefined,
    entertainmentId,
  } = {}) => {
    try {
      const response = await sendRequest({
        url: '/api/v1/lasertag-games/assignments',
        method: 'DELETE',
        headers: authCtx.getAuthHeaders(),
        params: {
          bookingId,
          teamCount,
          ...(entertainmentId ? { entertainmentId } : {}),
          ...(slot !== undefined ? { slot } : {}),
        },
      });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const getLasertagGamesForOperator = async ({ entertainmentId, status, since }) => {
    try {
      const params = {
        entertainmentId,
        ...(status ? { status } : {}),
        ...(since ? { since } : {}),
      };
      const response = await sendRequest({
        url: `/api/v1/lasertag-games/operator`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
        params,
      });
      return response?.data || [];
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const getLasertagGameStatistics = async ({ entertainmentId, date }) => {
    try {
      const params = {
        entertainmentId,
        date,
      };
      const response = await sendRequest({
        url: `/api/v1/lasertag-games/statistics`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
        params,
      });
      return response?.data || response || null;
    } catch (err) {
      setError(err);
      throw err;
    }
  };
  return {
    isLoading,
    error,
    setError,
    startGame,
    endGame,
    updateMember,
    getMembersByIds,
    getOperatorContext,
    getParticipantsForPreparing,
    getLastAssignmentBySlot,
    updateGroupAssignment,
    deleteGroupAssignment,
    updateSplitQuantities,
    confirmPendingCheckin,
    declinePendingCheckin,
    acceptLateCheckin,
    updateSitOutForPreparing,
    updateInformedForPreparing,
    updateGroupApproval,
    getLasertagGamesForOperator,
    getLasertagGameStatistics,
  };
};

export default useLasertagGame;
